import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import {
  Column,
  ColumnBodyOptions,
  ColumnEditorOptions,
} from 'primereact/column'
import {
  DataTableDataSelectableEvent,
  DataTableEditingRows,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowEditSaveEvent,
  DataTableRowEditValidatorOptions,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable'
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'

import { useMode } from '../../../../../../../hooks/useMode'
import {
  ConfigurableComponent,
  ConfigurableTable,
} from '../../../../../../../utils/wrapper'
import {
  CompanyCodeCustomField,
  CustomFieldDefinition,
} from '../../../../suppliers'
import { FormValues } from '../common'

type Props = {
  formik: FormikProps<FormValues>
  config: any
  getNestedFormErrorMessage: (
    index: number,
    fieldName: string,
  ) => false | JSX.Element
  availableCustomFieldsOptions?: CustomFieldDefinition[]
  onEditingModeChange?: (isEditing: boolean) => void
}

export const AdditionalProperties: React.FC<Props> = ({
  formik,
  config,
  getNestedFormErrorMessage,
  availableCustomFieldsOptions,
  onEditingModeChange,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const mode = useMode()

  const [editingRows, setEditingRows] = useState<DataTableEditingRows>({})
  const [selectedCustomFields, setSelectedCustomFields] =
    useState<CompanyCodeCustomField[]>()

  const isEditingMode = Object.keys(editingRows).length > 0

  // Notify parent component about editing mode changes
  useEffect(() => {
    onEditingModeChange?.(isEditingMode)
  }, [isEditingMode, onEditingModeChange])

  // Check if there are available options for new rows
  const usedCustomFieldIds = formik.values.customFields
    ?.map(field => field.CustomFieldDefinition?.Id)
    .filter(id => id) // Remove empty/undefined IDs

  const hasAvailableOptions =
    availableCustomFieldsOptions?.some(
      option => !usedCustomFieldIds?.includes(option.Id),
    ) || false

  const handleAddNewRow = async () => {
    const newRowIndex = formik.values.customFields?.length

    // Check if there's only one available option left
    const availableOptions = availableCustomFieldsOptions?.filter(
      option => !usedCustomFieldIds?.includes(option.Id),
    )

    const shouldAutoSelect = availableOptions?.length === 1
    const selectedOption = shouldAutoSelect ? availableOptions[0] : undefined

    // Set default value based on data type if auto-selecting
    const getDefaultValue = (datatype?: string) => {
      switch (datatype) {
        case 'Number':
          return '0'
        case 'Boolean':
          return 'false'
        case 'DateTime':
          return new Date().toISOString()
        case 'String':
        default:
          return ''
      }
    }

    const newRowData: CompanyCodeCustomField = {
      Id: `newCustomField-${newRowIndex}`,
      Value: getDefaultValue(selectedOption?.Datatype),
      CustomFieldDefinition: selectedOption || {
        Key: '',
        Type: 'CompanyCode',
        Datatype: 'String',
        Id: '',
      },
    }

    await formik.setFieldValue(`customFields.${newRowIndex}`, newRowData)
    setEditingRows(prev => ({
      ...prev,
      [newRowData.Id!]: true,
    }))
  }

  const handleDeleteRows = async (customFields: CompanyCodeCustomField[]) => {
    if (customFields) {
      const newCustomFields = formik.values.customFields?.filter(
        customField =>
          !customFields.find(
            fieldToDelete => fieldToDelete.Id === customField.Id,
          ),
      )
      await formik.setFieldValue('customFields', newCustomFields)

      // Only remove the deleted elements from the selection
      setSelectedCustomFields(
        prevSelected =>
          prevSelected?.filter(
            selected =>
              !customFields.find(deleted => deleted.Id === selected.Id),
          ) || [],
      )
    }
  }

  const headerActions = () => {
    if (mode === 'add' || mode === 'edit')
      return (
        <>
          <Button
            label={t(
              'masterDataManagement.companyCodes.table.dialog.form.additionalProperties.table.delete.label',
            )}
            onClick={() => handleDeleteRows(selectedCustomFields!)}
            icon="pi pi-trash"
            outlined
            disabled={!selectedCustomFields?.length || isEditingMode}
          />

          <Button
            label={t(
              'masterDataManagement.companyCodes.table.dialog.form.additionalProperties.table.add.label',
            )}
            onClick={handleAddNewRow}
            icon="pi pi-plus"
            outlined
            disabled={isEditingMode || !hasAvailableOptions}
          />
        </>
      )
  }

  // Key editor
  const keyEditor = (options: ColumnEditorOptions) => {
    const selectedKeyTemplate = (
      option: CustomFieldDefinition,
      props: DropdownProps,
    ) => {
      if (option) return keyOptionTemplate(option)

      return <span>{props.placeholder}</span>
    }

    const keyOptionTemplate = (option: CustomFieldDefinition) => {
      return <span>{option.Key}</span>
    }

    // Filter options dynamically for this specific row
    const currentRowValue =
      formik.values.customFields?.[options.rowIndex]?.CustomFieldDefinition
    const usedCustomFieldIds = formik.values.customFields
      ?.map(field => field.CustomFieldDefinition?.Id)
      .filter(id => id) // Remove empty/undefined IDs

    const filteredOptions = availableCustomFieldsOptions?.filter(option => {
      // Include if not used by any other row, OR if it's the current value of this row
      return (
        !usedCustomFieldIds?.includes(option.Id) ||
        option.Id === currentRowValue?.Id
      )
    })

    const handleChange = (event: DropdownChangeEvent) => {
      const customFieldDefinition: CustomFieldDefinition =
        event.target.value || ''

      formik.setFieldValue(
        `customFields.${options.rowIndex}.CustomFieldDefinition`,
        customFieldDefinition,
      )

      const dataType = customFieldDefinition.Datatype
      let newValue = ''
      switch (dataType) {
        case 'String':
          newValue = ''
          break
        case 'Number':
          newValue = '0'
          break
        case 'Boolean':
          newValue = 'false'
          break
        case 'DateTime':
          newValue = new Date().toISOString()
          break
        default:
          break
      }

      formik.setFieldValue(`customFields.${options.rowIndex}.Value`, newValue)
    }

    const keyComponent = (
      <Dropdown
        autoFocus
        id="key"
        value={currentRowValue} // {options.value}
        onChange={handleChange}
        options={filteredOptions}
        optionLabel="Key"
        className="w-full"
        valueTemplate={selectedKeyTemplate}
        itemTemplate={keyOptionTemplate}
      />
    )

    const keyErrorMsg = (
      <div>
        {getNestedFormErrorMessage(
          options.rowIndex,
          'CustomFieldDefinition.Key',
        )}
      </div>
    )

    const keyWrapper = (
      <ConfigurableComponent
        key={`key-${options.rowIndex}`}
        config={config}
        component={keyComponent}
        errorMsg={keyErrorMsg}
        className="m-0"
      />
    )

    return <>{keyWrapper}</>
  }

  // Value editor
  const valueEditor = (options: ColumnEditorOptions) => {
    const handleChange = (
      value: string | boolean | number | Date | null | undefined,
    ): void => {
      if (value === null || value === undefined) return

      formik.setFieldValue(
        `customFields.${options.rowIndex}.Value`,
        value.toString(),
      )
    }

    const valueComponent = () => {
      const dataType =
        formik.values.customFields?.[options.rowIndex].CustomFieldDefinition
          .Datatype

      switch (dataType) {
        case 'String':
          return (
            <InputText
              id="value"
              className="w-full"
              value={formik.values.customFields?.[options.rowIndex].Value}
              onChange={event => handleChange(event.target.value)}
            />
          )
        case 'Number':
          return (
            <InputNumber
              id="value"
              className="w-full"
              value={Number(
                formik.values.customFields?.[options.rowIndex].Value,
              )}
              onChange={event => handleChange(event.value)}
              locale={locale}
              maxFractionDigits={3}
              showButtons
            />
          )
        case 'Boolean':
          return (
            <InputSwitch
              id="value"
              checked={
                formik.values.customFields?.[options.rowIndex].Value === 'true'
              }
              onChange={event => {
                handleChange(event.value)
              }}
            />
          )
        case 'DateTime':
          return (
            <Calendar
              id="value"
              className="w-full"
              value={
                new Date(formik.values.customFields?.[options.rowIndex].Value)
              }
              onChange={event => handleChange(event.value)}
            />
          )
        default:
          return <></>
      }
    }

    const valueErrorMsg = (
      <div>{getNestedFormErrorMessage(options.rowIndex, 'Value')}</div>
    )

    const valueWrapper = (
      <ConfigurableComponent
        config={config}
        component={valueComponent()}
        errorMsg={valueErrorMsg}
        className="m-0"
      />
    )

    return <>{valueWrapper}</>
  }

  // Value body template
  const valueBodyTemplate = (rowData: CompanyCodeCustomField) => {
    const dataType = rowData.CustomFieldDefinition.Datatype

    switch (dataType) {
      case 'Number':
        return <span>{Number(rowData.Value).toLocaleString(locale)}</span>
      case 'Boolean':
        return <InputSwitch checked={rowData.Value === 'true'} />
      case 'DateTime':
        return <>{new Date(rowData.Value).toLocaleDateString(locale)}</>
      case 'String':
      default:
        return <>{rowData.Value}</>
    }
  }

  //  Action template
  const actionBodyTemplate = (
    rowData: CompanyCodeCustomField,
    options: ColumnBodyOptions,
  ) => {
    if (options.rowEditor?.editing)
      return (
        <>
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onCancelClick) {
                options.rowEditor.onCancelClick(event)
              }
            }}
          />

          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onSaveClick) {
                options.rowEditor.onSaveClick(event)
              }
            }}
          />
        </>
      )

    if (mode === 'add' || mode === 'edit') {
      return (
        <>
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-text"
            severity="danger"
            onClick={async () => {
              await handleDeleteRows([rowData])
            }}
            disabled={isEditingMode}
          />

          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onInitClick) {
                options.rowEditor.onInitClick(event)
              }
            }}
            disabled={isEditingMode}
          />
        </>
      )
    }
  }

  const onRowEditInit = (event: DataTableRowEditEvent) => {}

  const onRowEditChange = (event: DataTableRowEditEvent) => {
    setEditingRows(event.data)
  }

  const onRowEditCancel = (event: DataTableRowEditEvent) => {
    // If the custom field is newly added it has the id "newCustomField-X" we need to remove this from formik.
    // If it's "customField-X", it should never be deleted.
    // Otherwise it was already added but the user wants to cancel the editing and remove the changes.
    const isNewCustomField = event.data.Id === `newCustomField-${event.index}`
    const isTemporaryCustomField =
      event.data.Id === `customField-${event.index}`

    if (isNewCustomField) {
      // Remove completely new custom fields
      const updatedCustomFields = formik.values.customFields?.filter(
        customField => customField.Id !== event.data.Id,
      )
      formik.setFieldValue('customFields', updatedCustomFields)
    } else if (isTemporaryCustomField) {
      // For customField-X, do nothing - keep the field as is, no deletion, no reset
      // The field stays in its current state
    } else {
      // For existing fields, reset to original value
      const originalCustomField =
        formik.initialValues.customFields?.[event.index]
      formik.setFieldValue(`customFields.${event.index}`, originalCustomField)
    }
  }

  const rowEditValidator = (
    data: DataTableValue,
    options: DataTableRowEditValidatorOptions<DataTableValueArray>,
  ) => {
    // Trigger validation for this specific field
    formik.setFieldTouched(`customFields.${options.rowIndex}.Value`, true)
    formik.setFieldTouched(
      `customFields.${options.rowIndex}.CustomFieldDefinition.Key`,
      true,
    )
    // Check for validation errors
    const customFieldErrors = formik.errors.customFields as any
    const rowErrors = customFieldErrors?.[options.rowIndex]
    const hasErrors = rowErrors && Object.keys(rowErrors).length > 0
    const isValid = !hasErrors

    return isValid
  }

  const onRowEditSave = (
    event: DataTableRowEditSaveEvent<DataTableValueArray>,
  ) => {
    const isNewCustomField = event.data.Id === `newCustomField-${event.index}`

    if (isNewCustomField && event.valid) {
      // Change the Id from newCustomField-X to customField-X
      formik.setFieldValue(
        `customFields.${event.index}.Id`,
        `customField-${event.index}`,
      )
    }
  }

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    setEditingRows({})
  }

  const isDataSelectable = (
    event: DataTableDataSelectableEvent,
  ): boolean | null => {
    return !isEditingMode
  }

  return (
    <ConfigurableTable
      config={config.table}
      data={formik.values.customFields}
      headerActions={headerActions()}
      stateKey="table-company-code-custom-fields"
      checkboxen={mode === 'add' || mode === 'edit'}
      isDataSelectable={isDataSelectable}
      selected={selectedCustomFields}
      setSelected={setSelectedCustomFields}
      dataKey="Id"
      editMode="row"
      editingRows={editingRows}
      // Callbacks
      onRowEditInit={onRowEditInit}
      onRowEditChange={onRowEditChange}
      onRowEditCancel={onRowEditCancel}
      rowEditValidator={rowEditValidator}
      onRowEditSave={onRowEditSave}
      onRowEditComplete={onRowEditComplete}
      className="w-full"
    >
      <Column
        key="CustomFieldDefinition.Key"
        field="CustomFieldDefinition.Key"
        header="Key"
        editor={keyEditor}
        className="w-6"
      />
      <Column
        key="Value"
        field="Value"
        header="Value"
        editor={valueEditor}
        body={valueBodyTemplate}
        className="w-6"
      />
      <Column
        rowEditor
        key="buttons"
        align="right"
        body={actionBodyTemplate}
        className="flex no-wrap gap-2"
      />
    </ConfigurableTable>
  )
}
